import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { STRINGS } from '@/strings/strings';
import { useWorldSelectionSheet } from '@/components/worlds/useWorldSelectionSheet';
import { cn } from '@/lib/utils';
import type { ShareMode } from '@/features/story/share/variants';
import type { SeriesEpisodeContext } from '@/features/series/useSeriesEpisodeContext';

interface Props {
  shareMode: ShareMode;
  onShareClick: () => void;
  /** Override classes on the primary share button (e.g. add a white
   *  border on KP's navy bg where the default `bg-dream-navy` button
   *  blends into the page bg with no visible shape). */
  primaryClassName?: string;
  /** Override classes on both secondary buttons (Neue Geschichte +
   *  Bibliothek) — e.g. swap default navy border + navy text to white
   *  for visibility on KP's navy bg. */
  secondaryClassName?: string;
  /** B3: when this story is a series episode, the secondary row is
   *  replaced with series CTAs (next episode / new season + season
   *  overview). The share primary stays unchanged. */
  series?: SeriesEpisodeContext;
}

/**
 * Footer action bar: full-width share CTA + two secondary buttons
 * ("Neue Geschichte" → opens WorldSelectionSheet, "Bibliothek" →
 * /library). Share CTA label switches between "📖 Geschichte teilen"
 * and "🧠 Wissensblatt teilen" per `shareMode` — resolved by the
 * caller via `resolveStoryMode(storyType)`.
 *
 * "Neue Geschichte" opens the global WorldSelectionSheet via
 * `useWorldSelectionSheet().openSheet()` (Batch 10 Sub-phase D —
 * Decision #13 Path A; the bare /builder route was removed).
 *
 * `primaryClassName` / `secondaryClassName` are world-route overrides
 * for KP Story-Output where the default navy fills/borders/text
 * disappear into the navy page bg (PWA #16 Sub-Phase 16.3 Phase 4b
 * iteration). AA + SB Story-Output don't pass these — defaults read
 * fine on Light-Blue and Cream world bgs.
 */
export function StoryFooterActions({
  shareMode,
  onShareClick,
  primaryClassName,
  secondaryClassName,
  series,
}: Props) {
  const navigate = useNavigate();
  const { openSheet } = useWorldSelectionSheet();
  const footerCopy = STRINGS.storyOutput.page.footer;
  const share = STRINGS.storyOutput.page.share.triggerPrimary;
  const triggerLabel = shareMode === 'sb_explanation' ? share.sbExplanation : share.standard;
  const seriesCopy = STRINGS.series.output;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onShareClick}
        className={cn(
          'h-11 w-full rounded-lg bg-dream-navy text-sm font-medium text-dream-white transition hover:bg-dream-navy/90',
          primaryClassName,
        )}
      >
        {triggerLabel}
      </button>

      {series ? (
        <div className="space-y-2">
          {series.isLast ? (
            <button
              type="button"
              onClick={series.onNewSeason}
              className="h-11 w-full rounded-lg bg-dream-gold text-sm font-bold text-dream-navy transition hover:bg-dream-gold/90"
            >
              {seriesCopy.newSeason}
            </button>
          ) : !series.nextEpisodeGenerated ? (
            // #6: only offer "Nächste Folge erstellen" while the next episode
            // (episodeNumber + 1) is not yet generated. Once it exists, this
            // disappears (the user reaches it via "Zur Staffel").
            <button
              type="button"
              onClick={series.onNextEpisode}
              disabled={series.generatingNext}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-dream-gold text-sm font-bold text-dream-navy transition hover:bg-dream-gold/90 disabled:opacity-60"
            >
              {series.generatingNext && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              {seriesCopy.nextEpisode}
            </button>
          ) : null}
          <button
            type="button"
            onClick={series.onToSeason}
            className={cn(
              'h-11 w-full rounded-lg border border-dream-navy bg-transparent text-sm font-medium text-dream-navy transition hover:bg-dream-navy/5',
              secondaryClassName,
            )}
          >
            {seriesCopy.toSeason}
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openSheet}
            className={cn(
              'h-11 flex-1 rounded-lg border border-dream-navy bg-transparent text-sm font-medium text-dream-navy transition hover:bg-dream-navy/5',
              secondaryClassName,
            )}
          >
            {footerCopy.newStory}
          </button>
          <button
            type="button"
            onClick={() => navigate('/library')}
            className={cn(
              'h-11 flex-1 rounded-lg border border-dream-navy bg-transparent text-sm font-medium text-dream-navy transition hover:bg-dream-navy/5',
              secondaryClassName,
            )}
          >
            {footerCopy.library}
          </button>
        </div>
      )}
    </div>
  );
}
